import { useQuery } from "@tanstack/react-query";

import { adminApi } from "apis";
import type { PatientsPaginationRequestDto } from "libs/cellera-sdk";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoSortByEnum,
  PatientsPaginationRequestDtoSortOrderEnum,
  PatientsPaginationRequestDtoStatusEnum,
} from "libs/cellera-sdk";
import type { Patient } from "types";
import { mapPatientDtoToPatient, parseUTCDate } from "utils";

export interface GetPatientsParams {
  page: number;
  pageSize: number;
  search?: string;
  gender?: PatientsPaginationRequestDtoGenderEnum[];
  status?: PatientsPaginationRequestDtoStatusEnum[];
  birthDateFrom?: string;
  birthDateTo?: string;
  sortBy?: PatientsPaginationRequestDtoSortByEnum;
  sortOrder?: PatientsPaginationRequestDtoSortOrderEnum;
  // Scopes the query to a single practice. Required for cross-practice data
  // isolation (HIPAA) on screens where an admin acts within one practice
  // context (e.g. eFax assignment). Sent to BE so it can constrain results.
  practiceId?: string;
}

interface UsePatientsResult {
  patients: Patient[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePatients = (params: GetPatientsParams): UsePatientsResult => {
  const queryKey = [
    "admin-patients",
    params.page,
    params.pageSize,
    params.search,
    params.gender?.join(","),
    params.status?.join(","),
    params.birthDateFrom,
    params.birthDateTo,
    params.sortBy,
    params.sortOrder,
    params.practiceId,
  ];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await adminApi.getAllPatients({
        // `practiceId` is not yet typed on the auto-generated DTO. It is sent
        // so the BE can scope results to a single practice once supported;
        // until then the BE may ignore it and the caller must additionally
        // guard against cross-practice leaks at the action layer.
        patientsPaginationRequestDto: {
          pageSize: params.pageSize || 10,
          page: (params.page || 0) + 1,
          search: params.search,
          gender:
            params.gender && params.gender.length > 0
              ? params.gender
              : undefined,
          status:
            params.status && params.status.length > 0
              ? params.status
              : undefined,
          birthDateFrom: params.birthDateFrom
            ? parseUTCDate(params.birthDateFrom)
            : undefined,
          birthDateTo: params.birthDateTo
            ? parseUTCDate(params.birthDateTo)
            : undefined,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          ...(params.practiceId ? { practiceId: params.practiceId } : {}),
        } as PatientsPaginationRequestDto & { practiceId?: string },
      });

      return {
        patients: (response.patients ?? []).map(mapPatientDtoToPatient),
        totalCount:
          response.pagination?.total ?? response.patients?.length ?? 0,
      };
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    patients: data?.patients ?? [],
    totalCount: data?.totalCount ?? 0,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};

export default usePatients;
